/**
 * Droppable group section for organizing modules within module groups.
 * Supports drag-and-drop module reordering and group editing/deletion.
 */
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface GroupData {
	id: string;
	name: string | null;
	order: number;
	separatorAfter: boolean;
}

interface GroupSectionProps {
	group: GroupData;
	moduleIds: string[];
	children: ReactNode;
	onEdit: (group: GroupData) => void;
	onDelete: (group: GroupData) => void;
}

/**
 * Renders a draggable/droppable group section for module organization.
 * @param props - component props
 * @param props.group - the group data
 * @param props.moduleIds - IDs of modules in this group
 * @param props.children - child components (module items)
 * @param props.onEdit - callback when edit is clicked
 * @param props.onDelete - callback when delete is clicked
 */
export function GroupSection({
	group,
	moduleIds,
	children,
	onEdit,
	onDelete,
}: GroupSectionProps) {
	const {
		attributes,
		listeners,
		setNodeRef: setSortableRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: `group-${group.id}` });

	const { setNodeRef: setDroppableRef, isOver } = useDroppable({
		id: `droppable-${group.id}`,
		data: { groupId: group.id },
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div
			ref={setSortableRef}
			style={style}
			className={cn(
				"rounded-lg border bg-card/50",
				isDragging && "z-50 shadow-lg"
			)}
		>
			{/* Group Header */}
			<div className="flex items-center gap-2 border-b px-3 py-2">
				{/* Drag Handle */}
				<button
					{...attributes}
					{...listeners}
					className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
				>
					<GripVertical className="h-4 w-4" />
				</button>

				{/* Group Name */}
				<div className="flex-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
					{group.name ?? "(Unnamed Group)"}
				</div>

				{/* Separator Indicator */}
				{group.separatorAfter && (
					<span className="rounded bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
						Separator
					</span>
				)}

				{/* Actions */}
				<div className="flex gap-1">
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onClick={() => { onEdit(group); }}
					>
						<Pencil className="h-3 w-3" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-red-400 hover:text-red-400"
						onClick={() => { onDelete(group); }}
					>
						<Trash2 className="h-3 w-3" />
					</Button>
				</div>
			</div>

			{/* Modules Drop Zone */}
			<div
				ref={setDroppableRef}
				className={cn(
					"min-h-[60px] space-y-2 p-3",
					isOver && "bg-accent/50"
				)}
			>
				<SortableContext items={moduleIds} strategy={verticalListSortingStrategy}>
					{children}
				</SortableContext>
				{moduleIds.length === 0 && (
					<div className="flex h-[60px] items-center justify-center rounded-lg border-2 border-dashed text-sm text-muted-foreground">
						Drop modules here
					</div>
				)}
			</div>

			{/* Separator Preview */}
			{group.separatorAfter && (
				<div className="mx-3 mb-3 h-px bg-border" />
			)}
		</div>
	);
}
